/**
 * Turn `deck.details.synergy.list` into an entry-level graph. Each card
 * becomes a container node and each of its triggers/abilities/statics/
 * replacements becomes its own entry node. Edges come in two flavors:
 *
 *   - 'owns' edges pin every entry to its parent card with a short stiff
 *     spring so the entries cluster visually around their card.
 *   - 'ref' edges fan out from an entry to the cards it references
 *     (sources, enablers, replacements, targets, cardsOfSupporting*). A
 *     single pair of entry→card gets its weight summed across ref groups.
 *
 * The nodeLimit applies to *card containers*, not total nodes — so a limit
 * of 30 gives you 30 cards plus all of their entries. We drop cards that
 * participate in zero ref edges (after the trim) so the force sim doesn't
 * fling isolated self-tethered clusters off to infinity.
 */

import { SYNERGY_CATEGORY_ORDER } from './synergyCategoryConstants';

const DEFAULT_NODE_LIMIT = 25;

// The synergy view has two filter axes that together pick the edges we
// render:
//
//   INPUT (what feeds the entry)  →  CONSUMER (what kind of entry it is)
//
// An entry's category (triggers/abilities/statics/replacements) is what
// *consumes* the cards listed in its ref groups (sources/enablers/…).
// The user picks both axes in the toolbar and we emit ref-edges whose
// source entry matches BOTH the consumer filter and the input filter.
//
// "Supporting" bundles type/color/textpart variants since they all
// express "a card of a matching type/color/text supports this entry".

export const SYNERGY_INPUT_GROUPS = {
    all: {
        label: 'Any input',
        groups: [
            'sources',
            'enablers',
            'replacements',
            'targets',
            'cardsOfSupportingType',
            'cardsOfSupportingColor',
            'textPartCardsOfSupportingType',
        ],
    },
    sources: { label: 'Trigger sources', groups: ['sources'] },
    enablers: { label: 'Enablers', groups: ['enablers'] },
    replacements: { label: 'Replacement effects', groups: ['replacements'] },
    supporting: {
        label: 'Supporting cards',
        groups: ['cardsOfSupportingType', 'cardsOfSupportingColor', 'textPartCardsOfSupportingType'],
    },
    targets: { label: 'Targets', groups: ['targets'] },
};

export const SYNERGY_CONSUMER_CATEGORIES = {
    all: { label: 'All entries', categories: ['triggers', 'abilities', 'statics', 'replacements'] },
    triggers: { label: 'Triggers', categories: ['triggers'] },
    abilities: { label: 'Abilities', categories: ['abilities'] },
    statics: { label: 'Statics', categories: ['statics'] },
    replacements: { label: 'Replacement entries', categories: ['replacements'] },
};

export const SYNERGY_INPUT_KEYS = Object.keys(SYNERGY_INPUT_GROUPS);
export const SYNERGY_CONSUMER_KEYS = Object.keys(SYNERGY_CONSUMER_CATEGORIES);

function resolveInputGroups(inputKey) {
    const entry = SYNERGY_INPUT_GROUPS[inputKey] || SYNERGY_INPUT_GROUPS.all;
    return entry.groups;
}

function resolveConsumerCategories(consumerKey) {
    const entry = SYNERGY_CONSUMER_CATEGORIES[consumerKey] || SYNERGY_CONSUMER_CATEGORIES.all;
    return entry.categories;
}

function extractId(candidate) {
    if (!candidate) return null;
    if (typeof candidate === 'string') return candidate;
    return candidate.id || candidate.cardId || null;
}

function dominantCategory(catCounts) {
    let best = null;
    let bestCount = -1;
    Object.entries(catCounts || {}).forEach(([cat, count]) => {
        if (count > bestCount) {
            best = cat;
            bestCount = count;
        }
    });
    return best;
}

export function buildEntrySynergyGraph(synergyList, options = {}) {
    const nodeLimit = options.nodeLimit || DEFAULT_NODE_LIMIT;
    const inputGroups = resolveInputGroups(options.input);
    const consumerCategories = new Set(resolveConsumerCategories(options.consumer));
    if (!synergyList || typeof synergyList !== 'object') {
        return { nodes: [], edges: [] };
    }

    const cardAgg = new Map();
    const entries = [];

    const touchCard = (id) => {
        if (!id) return null;
        if (!cardAgg.has(id)) {
            cardAgg.set(id, { id, score: 0, entryCount: 0, refCount: 0, catCounts: {} });
        }
        return cardAgg.get(id);
    };

    Object.keys(synergyList).forEach((ownerId) => {
        const node = synergyList[ownerId];
        if (!node) return;
        const owner = touchCard(ownerId);
        SYNERGY_CATEGORY_ORDER.forEach((cat) => {
            const bucket = node[cat] || {};
            Object.keys(bucket).forEach((entryKey) => {
                const entry = bucket[entryKey];
                if (!entry) return;
                const score = Number(entry?.conditionScoring?.total || 0);
                owner.entryCount += 1;
                owner.score += score;
                owner.catCounts[cat] = (owner.catCounts[cat] || 0) + 1;

                // Entries outside the consumer filter still contribute to
                // the card's ranking (so the top-N pool stays stable as
                // the user toggles filters), but we skip building an
                // entry node / ref edges for them.
                if (!consumerCategories.has(cat)) return;

                const refCounts = new Map();
                inputGroups.forEach((group) => {
                    const list = entry[group];
                    if (!Array.isArray(list)) return;
                    list.forEach((src) => {
                        const refId = extractId(src);
                        if (!refId || refId === ownerId) return;
                        refCounts.set(refId, (refCounts.get(refId) || 0) + 1);
                    });
                });

                const refs = [];
                refCounts.forEach((weight, refId) => {
                    refs.push({ cardId: refId, weight });
                    touchCard(refId).refCount += 1;
                });

                entries.push({
                    id: `${ownerId}::${cat}::${entryKey}`,
                    cardId: ownerId,
                    category: cat,
                    entryKey,
                    text: entry?.text || '',
                    score,
                    refs,
                });
            });
        });
    });

    const rankedCards = Array.from(cardAgg.values())
        .sort((a, b) => b.score - a.score || b.refCount - a.refCount)
        .slice(0, nodeLimit);
    const keepCardIds = new Set(rankedCards.map((c) => c.id));

    const edges = [];
    const edgeKeys = new Set();
    const externallyConnectedCardIds = new Set();

    const recordEdge = (source, target, weight, kind) => {
        if (!source || !target) return;
        const key = `${kind}::${source}::${target}`;
        if (edgeKeys.has(key)) return;
        edgeKeys.add(key);
        edges.push({ source, target, weight, kind });
    };

    entries.forEach((e) => {
        if (!keepCardIds.has(e.cardId)) return;
        e.refs.forEach((r) => {
            if (!keepCardIds.has(r.cardId)) return;
            recordEdge(e.id, r.cardId, r.weight, 'ref');
            externallyConnectedCardIds.add(e.cardId);
            externallyConnectedCardIds.add(r.cardId);
        });
    });

    entries.forEach((e) => {
        if (!keepCardIds.has(e.cardId)) return;
        if (!externallyConnectedCardIds.has(e.cardId)) return;
        recordEdge(e.id, e.cardId, 1, 'owns');
    });

    const cardNodes = rankedCards
        .filter((c) => externallyConnectedCardIds.has(c.id))
        .map((c) => ({
            id: c.id,
            kind: 'card',
            cardId: c.id,
            score: c.score,
            entryCount: c.entryCount,
            refCount: c.refCount,
            category: dominantCategory(c.catCounts),
        }));

    const keptCardIdSet = new Set(cardNodes.map((n) => n.id));
    const entryNodes = entries
        .filter((e) => keptCardIdSet.has(e.cardId))
        .map((e) => ({
            id: e.id,
            kind: 'entry',
            cardId: e.cardId,
            category: e.category,
            entryKey: e.entryKey,
            text: e.text,
            score: e.score,
        }));

    const finalNodeIds = new Set([...cardNodes.map((n) => n.id), ...entryNodes.map((n) => n.id)]);
    const finalEdges = edges.filter((e) => finalNodeIds.has(e.source) && finalNodeIds.has(e.target));

    return { nodes: [...cardNodes, ...entryNodes], edges: finalEdges };
}

/**
 * Counts ref-group contributions per (consumer, input) pair so the modal
 * can default to whichever pair dominates this deck. Returned as a flat
 * map keyed `consumer::input` → count.
 */
export function countSynergyPairings(synergyList) {
    const counts = {};
    if (!synergyList || typeof synergyList !== 'object') return counts;

    Object.keys(synergyList).forEach((ownerId) => {
        const node = synergyList[ownerId];
        if (!node) return;
        SYNERGY_CATEGORY_ORDER.forEach((cat) => {
            const bucket = node[cat] || {};
            Object.keys(bucket).forEach((entryKey) => {
                const entry = bucket[entryKey];
                if (!entry) return;
                SYNERGY_INPUT_KEYS.forEach((inputKey) => {
                    if (inputKey === 'all') return;
                    const groups = SYNERGY_INPUT_GROUPS[inputKey].groups;
                    let count = 0;
                    groups.forEach((group) => {
                        const list = entry[group];
                        if (Array.isArray(list)) count += list.length;
                    });
                    if (count > 0) {
                        const key = `${cat}::${inputKey}`;
                        counts[key] = (counts[key] || 0) + count;
                    }
                });
            });
        });
    });
    return counts;
}

export function pickDefaultPairing(synergyList) {
    const counts = countSynergyPairings(synergyList);
    let bestKey = null;
    let bestCount = 0;
    Object.entries(counts).forEach(([key, count]) => {
        if (count > bestCount) {
            bestKey = key;
            bestCount = count;
        }
    });
    if (!bestKey) return { consumer: 'all', input: 'all' };
    const [consumer, input] = bestKey.split('::');
    return { consumer, input };
}

/**
 * Which input keys actually have data for the given consumer in this
 * deck. Used to disable-or-hide impossible combinations in the Input
 * dropdown when the user picks a specific Consumer. 'all' is always
 * included as a valid input.
 */
export function getAvailableInputsForConsumer(synergyList, consumer) {
    const counts = countSynergyPairings(synergyList);
    const available = new Set(['all']);
    Object.keys(counts).forEach((key) => {
        const [cat, inputKey] = key.split('::');
        if (consumer === 'all' || consumer === cat) available.add(inputKey);
    });
    return available;
}
